import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white px-6 py-12 max-w-3xl mx-auto" data-testid="privacy-policy-page">
      <div className="mb-6">
        <Link to="/login" className="text-sm text-blue-600 hover:underline" data-testid="back-to-login-link">← Volver al inicio</Link>
      </div>
      <h1 className="text-3xl font-black text-[#111111] mb-2">Política de Privacidad</h1>
      <p className="text-sm text-[#52525B] mb-8">Última actualización: Abril 2026</p>

      <div className="space-y-6 text-sm text-[#333] leading-relaxed">
        <section>
          <p>
            Esta Política de Privacidad describe cómo <strong>Grupo Sach</strong> ("nosotros", "nuestro" o "la Empresa") recopila, utiliza y protege la información personal de los usuarios que interactúan con nuestras páginas de Facebook, números de WhatsApp Business y la plataforma CRM interna que usamos para gestionar la atención al cliente.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">1. Información que Recopilamos</h2>
          <p>Recopilamos la siguiente información a través de las integraciones con Facebook Messenger y WhatsApp Business API:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Nombre del perfil del usuario en la plataforma de mensajería (Facebook o WhatsApp)</li>
            <li>Identificador de usuario de la plataforma (Facebook Page-Scoped ID o número de WhatsApp)</li>
            <li>Foto de perfil pública (cuando esté disponible)</li>
            <li>Contenido de los mensajes enviados a nuestras páginas de Facebook o números de WhatsApp Business</li>
            <li>Archivos multimedia (imágenes, audios, documentos) que el usuario nos envíe</li>
            <li>Fecha, hora e idioma de los mensajes</li>
            <li>Datos de pedido voluntariamente proporcionados (nombre, dirección, teléfono) cuando el usuario realiza una compra</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">2. Uso de la Información</h2>
          <p>La información recopilada se utiliza exclusivamente para:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Responder a consultas y mensajes de los clientes</li>
            <li>Gestionar las conversaciones de atención al cliente desde un panel unificado</li>
            <li>Generar pedidos y coordinar envíos solicitados por los clientes</li>
            <li>Mantener un historial de conversaciones para mejorar la calidad del servicio</li>
            <li>Enviar respuestas automatizadas a través de un asistente de inteligencia artificial cuando sea apropiado</li>
            <li>Notificar al equipo cuando llega un mensaje nuevo (notificaciones push)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">3. Almacenamiento y Seguridad</h2>
          <p>
            Los datos se almacenan en servidores en la nube con acceso restringido y cifrado. Implementamos medidas de seguridad técnicas y organizativas para proteger la información personal, incluyendo:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Cifrado HTTPS/TLS en todas las comunicaciones</li>
            <li>Control de acceso basado en roles (Administrador, Supervisor, Usuario)</li>
            <li>Autenticación con contraseñas hasheadas (bcrypt)</li>
            <li>Tokens JWT con expiración para las sesiones</li>
            <li>Registros de auditoría (Kardex) de las acciones realizadas en el sistema</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">4. Compartición de Datos con Terceros</h2>
          <p>
            <strong>No vendemos, alquilamos ni intercambiamos</strong> información personal a terceros con fines de marketing. Los datos pueden ser compartidos únicamente con:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Meta Platforms, Inc.</strong> – para enviar y recibir mensajes a través de Facebook Messenger y WhatsApp Business API.</li>
            <li><strong>Proveedores de IA</strong> (OpenAI, Anthropic o Google) – únicamente el contenido del mensaje del cliente, sin datos personales identificables, para generar una respuesta automática cuando esté activado el bot de IA.</li>
            <li><strong>Servicios de transporte/envío</strong> – cuando el cliente realiza un pedido, sus datos de envío se comparten con la empresa de paquetería para entregar el producto.</li>
            <li><strong>Autoridades competentes</strong> – cuando sea legalmente requerido por orden judicial.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">5. Integración con la Plataforma de Meta</h2>
          <p>
            Nuestra aplicación utiliza las APIs de Facebook Messenger y WhatsApp Business proporcionadas por Meta Platforms, Inc. Al enviarnos un mensaje a través de estas plataformas, usted acepta tanto los <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Términos de Privacidad de Meta</a> como esta Política de Privacidad. El uso que hacemos de los datos cumple con las <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Políticas de la Plataforma de Meta</a> y las <a href="https://developers.facebook.com/devpolicy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Políticas para Desarrolladores</a>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">6. Retención de Datos</h2>
          <p>
            Conservamos los datos personales durante el tiempo necesario para cumplir con las finalidades para las que fueron recopilados:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Conversaciones:</strong> 24 meses desde el último mensaje</li>
            <li><strong>Pedidos:</strong> 5 años (por requisitos contables y fiscales)</li>
            <li><strong>Datos de contacto:</strong> hasta que el usuario solicite su eliminación</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">7. Derechos del Usuario</h2>
          <p>Los usuarios tienen derecho a:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Solicitar acceso a sus datos personales</li>
            <li>Solicitar la rectificación de datos inexactos o incompletos</li>
            <li>Solicitar la eliminación de sus datos personales (ver sección 8)</li>
            <li>Oponerse o limitar el tratamiento de sus datos</li>
            <li>Dejar de recibir mensajes en cualquier momento, escribiendo "STOP" o "BAJA" a nuestra página o número</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">8. Eliminación de Datos</h2>
          <p>
            Para solicitar la eliminación completa de sus datos personales, visite nuestra página de <Link to="/data-deletion" className="text-blue-600 underline" data-testid="data-deletion-link">Instrucciones para Eliminar Datos</Link> o envíenos un correo a <a href="mailto:complementossa@gmail.com" className="text-blue-600 underline">complementossa@gmail.com</a> con el asunto <em>"Eliminación de Datos"</em>. Procesaremos su solicitud dentro de los 30 días hábiles siguientes.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">9. Cambios en esta Política</h2>
          <p>
            Podemos actualizar esta Política de Privacidad ocasionalmente. La fecha de "Última actualización" en la parte superior indica cuándo se modificó por última vez. Los cambios significativos serán comunicados a través de nuestras páginas oficiales.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-[#111111] mb-2">10. Contacto</h2>
          <p>
            Si tiene preguntas, comentarios o reclamos sobre esta política o sobre el tratamiento de sus datos, contáctenos:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Empresa:</strong> Grupo Sach</li>
            <li><strong>Correo electrónico:</strong> <a href="mailto:complementossa@gmail.com" className="text-blue-600 underline">complementossa@gmail.com</a></li>
            <li><strong>Sitio web:</strong> <a href="https://crm.gruposach502.com" className="text-blue-600 underline">crm.gruposach502.com</a></li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
